import React from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { COMPATIBILITY_CHECK_URL, EMERGENCY, HOW_IT_WORKS } from '../../content/site';
import { CONTENT_WIDTH, PHI } from '../../styles/golden';
import { BRAND, outlineButtonSx } from '../../styles/ui';

const cardSx = {
  backgroundColor: 'var(--joan-surface)',
  border: `1px solid ${BRAND.line}`,
  borderRadius: '13px',
  boxShadow: BRAND.shadow
};

const HowItWorks: React.FC = () => (
  <Box
    component="section"
    id="how-it-works"
    aria-labelledby="how-it-works-title"
    sx={{
      backgroundColor: 'var(--joan-bg-tint)',
      color: BRAND.ink,
      px: { xs: 3, sm: 5 },
      py: { xs: 6, md: 7 },
      scrollMarginTop: { xs: 56, sm: 64 }
    }}
  >
    <Box sx={{ maxWidth: CONTENT_WIDTH.page, mx: 'auto' }}>
      <Typography id="how-it-works-title" variant="h2" sx={{ mb: 2 }}>
        How Joan works
      </Typography>
      <Typography variant="body1" sx={{ color: BRAND.muted, mb: { xs: 4, md: 5 }, maxWidth: CONTENT_WIDTH.text }}>
        Joan lets you alert someone you trust without picking up your phone or saying a word.
      </Typography>

      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 3
        }}
      >
        {HOW_IT_WORKS.map((step, index) => (
          <Box
            component="li"
            key={step.title}
            sx={{
              ...cardSx,
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: { xs: 3, sm: 3 }
            }}
          >
            <Box
              component="img"
              src={step.image}
              alt=""
              loading="lazy"
              sx={{ width: { xs: 89, md: 144 }, height: { xs: 89, md: 144 }, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
            />
            <Box>
              <Box
                aria-hidden
                sx={{
                  width: 21,
                  height: 21,
                  borderRadius: '50%',
                  backgroundColor: BRAND.accent,
                  color: 'var(--joan-surface)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: BRAND.muted }}>
                {step.text}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Safety note and requirements: φ : 1 */}
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `${PHI}fr 1fr` },
          gap: 3
        }}
      >
        <Box role="note" sx={{ ...cardSx, p: { xs: 3, md: 4 }, display: 'flex', gap: 2, backgroundColor: BRAND.warningSoft }}>
          <WarningAmberRoundedIcon sx={{ color: BRAND.warning, mt: 0.25 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
              {EMERGENCY.line}
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              An alert can't guarantee that someone will respond, for example if a contact's phone is switched off or has no signal.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, p: { xs: 3, md: 4 } }}>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            Before you start
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
            Joan works with an electricity smart meter. Check that your home is compatible.
          </Typography>
          <Button
            component={Link}
            href={COMPATIBILITY_CHECK_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ ...outlineButtonSx, textDecoration: 'none' }}
          >
            Compatibility check
          </Button>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default HowItWorks;
